import React, { useEffect, useMemo } from 'react';
import CardComponent from '../../../components/Card.component';
import { setHeaderTabs, setHeaderTitle } from '../../../redux/App/App.slice';
import { RoutesConstant } from '../../../constants/routes.constant';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import TableComponent from '../../../components/Table/Table.component';
import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';
import { useAppSelector } from '../../../hooks/useAppSelector.hook';
import { TableHeaderItemOrderProps } from '../../../components/Table/TableHeader.component';
import TableRowComponent from '../../../components/Table/TableRow.component';
import { DATE_FORMAT } from '../../../constants/date.contant';
import { debounce, uniqueId } from 'lodash';
import {
  addToQueryString,
  getKeywords,
  getPage,
  getSort,
  parseSearchParamsRouter,
} from '../../../services/router.service';
import NotAssignComponent from '../../../components/NotAssign.component';
import NoResultComponent from '../../../components/NoResult.component';
import FloatButtonComponent from '../../../components/FloatButton.component';
import PlusIcon from '../../../components/Icons/Plus.icon';
import { useModalManager } from '../../../hooks/ModalManager.hook';
import { SortTypes } from '../../../types/sort.types';
import { getInternshipListThunk } from '../../../redux/Internship/Internship.thunk';
import {
  getInternshipsList,
  getInternshipsMeta, resetInternshipSelectedAction,
} from '../../../redux/Internship/Internship.slice';
import { Internship } from '../../../redux/Internship/Internship.type';
import { Employee } from '../../../redux/Employee/Employee.type';
import UserBadgeComponent from '../../../components/UserBadge.component';
import { dateFormatter } from '../../../utils/formatters';
import InternshipEditModalComponent from '../../../components/Modal/Internship/InternshipEditModal.component';
import InternshipInfoModal from '../../../components/Modal/Internship/InternshipInfoModal';
import { StatusColor } from '../../../constants/statusColors.constant';
import BadgeComponent from '../../../components/Badge.component';
import { checkUserRequiredPermissions } from '../../../utils/getUserRolesByToken';
import { UserPermissions } from '../../../types/userPermissions.types';
import useAuthHook from '../../../hooks/useAuth.hook';
import i18n from 'i18next';
import {
  StyledInternshipMentors,
  StyledInternshipsPage,
} from './Internships.styles';
import { TABLE_HEADER_COLUMNS } from './constants/constants';

const InternshipsPage = () => {
  const dispatch = useAppDispatch();
  const meta = useAppSelector(getInternshipsMeta);
  const list = useAppSelector(getInternshipsList);
  const [_, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { group } = useParams();
  const { openModal } = useModalManager();
  const { user } = useAuthHook();

  useEffect(() => {
    dispatch(setHeaderTitle('Group internships'));
    if (!meta.items) return;
    dispatch(
      setHeaderTabs([
        {
          path: RoutesConstant.INTERNSHIPS_GROUPS.WAIT,
          text: 'header.tabs.internships.wait',
          badge: meta.items.WAIT_COUNTER,
        },
        {
          path: RoutesConstant.INTERNSHIPS_GROUPS.WORK,
          text: 'header.tabs.internships.work',
          badge: meta.items.WORK_COUNTER,
        },
        {
          path: RoutesConstant.INTERNSHIPS_GROUPS.CLOSED,
          text: 'header.tabs.internships.closed',
          badge: meta.items.CLOSED_COUNTER,
        },
      ]),
    );

    return () => {
      dispatch(setHeaderTabs([]));
    };
  }, [dispatch, meta]);

  const prepareRowColumns = (item: Internship) => {
    return [
      item.name,
      dateFormatter(item.testingAt, DATE_FORMAT),
      dateFormatter(item.startAt, DATE_FORMAT),
      dateFormatter(item.endAt, DATE_FORMAT),
      item.count,
      <BadgeComponent
        appearance="default"
        color={
          item.status &&
          item.status.color &&
          i18n.exists(`statusColor.${item.status.color}`)
            ? `STATUSES_${item.status.color.toUpperCase()}`
            : item.status?.color || `STATUSES_${StatusColor.GREY.toUpperCase()}`
        }
        textColor={
          item.status &&
          item.status.textColor &&
          i18n.exists(`statusColor.${item.status.textColor}`)
            ? `STATUSES_${item.status.textColor.toUpperCase()}`
            : item.status?.textColor ||
              `STATUSES_${StatusColor.GREY.toUpperCase()}`
        }
        text={item.status ? item.status.name : ''}
      />,
      <NotAssignComponent
        mode="badge"
        has={item.mentors ? item.mentors.length > 0 : false}
      >
        {item.mentors && (
          <StyledInternshipMentors>
            {item.mentors.map((mentor: Employee) => (
              <UserBadgeComponent info={mentor} key={mentor.id} />
            ))}
          </StyledInternshipMentors>
        )}
      </NotAssignComponent>,
    ];
  };

  const baseFilters = useMemo(() => {
    const params = parseSearchParamsRouter(window.location.search);
    delete params['keywords'];
    delete params['page'];
    delete params['sort'];
    return {
      filter: params || {},
    };
  }, [window.location.search]);

  const keywords = useMemo(() => {
    return getKeywords(window.location.search);
  }, [window.location.search]);

  const currentPage: number = useMemo<number>(() => {
    return getPage(window.location.search) || 1;
  }, [group, window.location.search]);

  const currentSort: string = useMemo<string>(() => {
    return getSort(window.location.search) || '-testing_at';
  }, [group, window.location.search]);

  useEffect(() => {
    handleChangePage(1);
  }, [group]);

  const handleGetList = (
    page: number,
    sort?: string,
    filters: any = undefined,
  ) => {
    dispatch(
      getInternshipListThunk({
        page,
        sort,
        ...(filters || baseFilters),
      }),
    );
  };

  const debounceSetSearchParams = debounce(setSearchParams, 200);

  const handleChangePage = (page: number) => {
    const search = addToQueryString([
      {
        name: 'page',
        value: page,
      },
      {
        name: 'sort',
        value: currentSort,
      },
    ]);
    debounceSetSearchParams(search, { replace: true });
    handleGetList(page, currentSort);
  };

  const handleChangeSort = ({
    sort,
    dataProperty,
  }: TableHeaderItemOrderProps) => {
    const sortDirective = sort === SortTypes.DESC ? '-' : '';
    const sortComplex = `${sortDirective}${dataProperty}`;

    const search = addToQueryString([{
      name: 'sort',
      value: sortComplex,
    }]);
    debounceSetSearchParams(search, { replace: true });
    handleGetList(currentPage, sortComplex);
  };

  const handleInternshipDbClick = (id: number | string) => {
    navigate(`${RoutesConstant.INTERNSHIP_R}/${id}`);
  };

  const handleInternshipClick = (id: number) => {
    openModal(
      InternshipInfoModal,
      { internshipId: id },
      { position: 'right-top' },
    );
  };

  return (
    <StyledInternshipsPage>
      <CardComponent>
        <TableComponent
          header={TABLE_HEADER_COLUMNS}
          loading={list.loading}
          pagination={{
            ...list.pagination,
            currentPage,
          }}
          onSortChange={handleChangeSort}
          onPageChange={handleChangePage}
        >
          {list.items.length > 0 ? (
            list.items.map((item: Internship) => (
              <TableRowComponent
                key={uniqueId('table-rows-')}
                columns={prepareRowColumns(item)}
                handleDBClick={() => handleInternshipDbClick(item.id)}
                handleClick={() => handleInternshipClick(item.id)}
              />
            ))
          ) : (
            <NoResultComponent />
          )}
        </TableComponent>
        {checkUserRequiredPermissions(user.permissions, [
          UserPermissions.CAN_MANAGE_INTERNSHIP_PEOPLE,
        ]) && (
          <FloatButtonComponent
            onClick={() => {
              dispatch(resetInternshipSelectedAction());
              openModal(
                InternshipEditModalComponent,
                { handleClose: () => handleChangePage(1) },
                { disableClose: false }
              );
            }}
          >
            <PlusIcon width={18} height={18} />
          </FloatButtonComponent>
        )}
      </CardComponent>
    </StyledInternshipsPage>
  );
};

export default InternshipsPage;
